import { initializeConsentMode } from './consentMode';

class GTMLoader {
  constructor() {
    this.gtmId = process.env.REACT_APP_GTM_ID;
    this.environment = process.env.REACT_APP_CURRENT_ENV || 'development';
    this.isLoaded = false;
    this.isValidated = false;
    this.debugMode = localStorage.getItem('gtm_debug') === 'true';
    this.consentModeInitialized = false;
  }

  // Validate environment and GTM ID combination
  validateEnvironment() {
    const validations = {
      isValidGTMId:
        this.gtmId &&
        this.gtmId.startsWith('GTM-') &&
        this.gtmId !== 'GTM-XXXXXXX',
      environmentMatch: true, // Will be set based on specific checks
      hasRequiredConfig: Boolean(this.gtmId && this.environment)
    };

    // Environment-specific validation
    if (this.environment === 'production') {
      // Accept both production GTM containers
      validations.environmentMatch =
        this.gtmId === 'GTM-PDX7VSST' || this.gtmId === 'GTM-NP9FFS3R';
      if (!validations.environmentMatch) {
        console.error(
          `**PROD** environment should use production GTM container (GTM-PDX7VSST or GTM-NP9FFS3R). Current: ${this.gtmId}`
        );
      }
    } else if (
      this.environment === 'development' ||
      this.environment === 'pre-production'
    ) {
      validations.environmentMatch = this.gtmId === 'GTM-TV2W4MS8';
      if (!validations.environmentMatch) {
        console.error(
          '**STG** environment should use development or pre-production GTM-ID container'
        );
      }
    } else {
      console.error('Unknown environment:', this.environment);
      validations.environmentMatch = false;
    }

    // Debug logging
    if (this.debugMode) {
      console.log('🔍 GTM Environment Validation:', {
        environment: this.environment,
        gtmId: this.gtmId,
        validations
      });
    }

    this.isValidated = Object.values(validations).every(Boolean);
    return this.isValidated;
  }

  // Load GTM script dynamically
  loadGTMScript() {
    return new Promise((resolve, reject) => {
      if (this.isLoaded) {
        resolve(true);
        return;
      }

      if (!this.validateEnvironment()) {
        reject(new Error('GTM environment validation failed'));
        return;
      }

      try {
        // IMPORTANT: Initialize Google Consent Mode v2 BEFORE GTM loads
        // This ensures cookieless pings are sent before user consent
        // NOTE: Consent mode is initialized in index.js on app load,
        // but we check here as a safety measure
        if (!window.gtag || !window.dataLayer) {
          initializeConsentMode();
          if (this.debugMode) {
            console.log(
              '✅ Consent Mode v2 initialized (fallback in GTM loader)'
            );
          }
        } else if (this.debugMode) {
          console.log('✅ Consent Mode v2 already initialized');
        }
        this.consentModeInitialized = true;

        // Initialize dataLayer if not exists
        window.dataLayer = window.dataLayer || [];

        // Create and inject GTM script
        const script = document.createElement('script');
        script.async = true;
        script.src = `https://www.googletagmanager.com/gtm.js?id=${this.gtmId}`;

        script.onload = () => {
          this.isLoaded = true;
          if (this.debugMode) {
            console.log('✅ GTM script loaded successfully:', this.gtmId);
          }
          resolve(true);
        };

        script.onerror = () => {
          console.error('❌ Failed to load GTM script');
          reject(new Error('Failed to load GTM script'));
        };

        // Inject GTM initialization code
        const initScript = document.createElement('script');
        initScript.innerHTML = `
          (function(w,d,s,l,i){
            w[l]=w[l]||[];
            w[l].push({'gtm.start': new Date().getTime(), event:'gtm.js'});
          })(window,document,'script','dataLayer','${this.gtmId}');
        `;

        // Add scripts to head
        document.head.appendChild(initScript);
        document.head.appendChild(script);

        // Add noscript fallback to body
        this.addNoScriptFallback();
      } catch (error) {
        console.error('❌ Error loading GTM:', error);
        reject(error);
      }
    });
  }

  // Add noscript fallback for users with JavaScript disabled
  addNoScriptFallback() {
    const noscriptEl = document.createElement('noscript');
    const iframe = document.createElement('iframe');

    iframe.src = `https://www.googletagmanager.com/ns.html?id=${this.gtmId}`;
    iframe.height = '0';
    iframe.width = '0';
    iframe.style.display = 'none';
    iframe.style.visibility = 'hidden';

    noscriptEl.appendChild(iframe);
    document.body.insertBefore(noscriptEl, document.body.firstChild);
  }

  // Check if GTM is ready to use
  isReady() {
    return this.isLoaded && this.isValidated && window.dataLayer;
  }

  // Get current configuration info
  getConfig() {
    return {
      gtmId: this.gtmId,
      environment: this.environment,
      isLoaded: this.isLoaded,
      isValidated: this.isValidated,
      debugMode: this.debugMode,
      consentModeInitialized: this.consentModeInitialized
    };
  }

  // Enable/disable debug mode
  setDebugMode(enabled) {
    this.debugMode = enabled;
    if (enabled) {
      localStorage.setItem('gtm_debug', 'true');
    } else {
      localStorage.removeItem('gtm_debug');
    }
  }

  // Force reload GTM (for testing purposes)
  async reload() {
    this.isLoaded = false;
    this.isValidated = false;

    // Remove existing GTM scripts
    const existingScripts = document.querySelectorAll(
      'script[src*="googletagmanager.com/gtm.js"]'
    );
    existingScripts.forEach((script) => script.remove());

    // Reload
    return this.loadGTMScript();
  }
}

// Create singleton instance
const gtmLoader = new GTMLoader();

export default gtmLoader;

// Export convenience methods
export const {
  loadGTMScript,
  validateEnvironment,
  isReady,
  getConfig,
  setDebugMode,
  reload
} = gtmLoader;
